import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../../api";
import { toast } from "sonner";
import { PanelHeading } from "./PortalShell";
import { RecipientPicker, emptyAudience, audienceValid } from "./RecipientPicker";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Send, Newspaper, MessageSquare, Loader2, Plus, Pencil, Trash2, Eye, Mail, Monitor,
  Paperclip, X,
} from "lucide-react";

const inp = "w-full border border-raf-sky px-3 py-2.5 outline-none focus:border-raf-blue text-sm";
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const escapeHtml = (value = "") => value
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#39;");

const textToHtml = (text = "") => escapeHtml(text)
  .split(/\n\n+/)
  .filter(Boolean)
  .map((part) => `<p style="margin:0 0 12px;">${part.replace(/\n/g, "<br/>")}</p>`)
  .join("");

const emailShell = (title, inner) => `<!doctype html><html><body style="margin:0;padding:24px;background:#EAF5F8;font-family:Arial,Helvetica,sans-serif;"><div style="max-width:600px;margin:0 auto;background:#fff;border:1px solid #d6e6ec;"><div style="background:#002F5F;padding:20px 28px;color:#fff;font-size:18px;font-weight:bold;">1471 Horwich Squadron RAF Air Cadets</div><div style="height:4px;background:#C60C30;"></div><div style="padding:28px;color:#071A2F;font-size:15px;line-height:1.6;"><h2 style="margin:0 0 14px;color:#002F5F;font-size:20px;">${escapeHtml(title)}</h2>${inner}</div></div></body></html>`;

const broadcastPreviewHtml = (title, body, bodyFormat, fromName = "Squadron Staff", attachments = []) => {
  const renderedBody = bodyFormat === "html" ? body : textToHtml(body);
  const renderedAttachments = attachments.length
    ? `<p><strong>Attachments</strong></p><ul style="padding-left:20px;">${attachments.map((a) => `<li style="margin:4px 0;">${escapeHtml(a.filename)}</li>`).join("")}</ul>`
    : "";
  return emailShell(title, `${renderedBody}${renderedAttachments}<p style="margin-top:20px;color:#5b6b78;font-size:13px;">&mdash; ${escapeHtml(fromName)}</p>`);
};

const AttachmentPicker = ({ attachments, setAttachments, testId = "comms-attachment" }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((a) => [...a, { id: data.id, filename: data.filename }]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="text-xs font-semibold text-raf-navy mb-1">Attachments (optional)</div>
      <input
        ref={fileRef}
        type="file"
        multiple
        data-testid={`${testId}-input`}
        className="hidden"
        onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
      />
      <button
        type="button"
        data-testid={`${testId}-add`}
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-raf-sky text-raf-blue hover:border-raf-blue transition-colors disabled:opacity-60"
      >
        {uploading ? <Loader2 className="animate-spin" size={13} /> : <Paperclip size={13} />} Add file
      </button>
      {attachments.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid={`${testId}-list`}>
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center gap-2 text-xs bg-raf-sky/40 px-2 py-1.5">
              <Paperclip size={12} className="text-raf-blue" />
              <span className="truncate text-raf-navy">{a.filename}</span>
              <button
                type="button"
                onClick={() => setAttachments((x) => x.filter((y) => y.id !== a.id))}
                className="ml-auto text-raf-red hover:text-raf-navy"
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-xs text-raf-slate">Files are sent as secure download links. Max 15MB each.</p>
    </div>
  );
};

const ChannelToggles = ({ channels, setChannels }) => {
  const toggle = (c) => setChannels((cs) => cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]);
  return (
    <div className="flex gap-2">
      <button type="button" data-testid="channel-dashboard" onClick={() => toggle("dashboard")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border transition-colors ${channels.includes("dashboard") ? "bg-raf-blue text-white border-raf-blue" : "bg-white text-raf-slate border-raf-sky"}`}>
        <Monitor size={14} /> Dashboard
      </button>
      <button type="button" data-testid="channel-email" onClick={() => toggle("email")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border transition-colors ${channels.includes("email") ? "bg-raf-blue text-white border-raf-blue" : "bg-white text-raf-slate border-raf-sky"}`}>
        <Mail size={14} /> Email
      </button>
    </div>
  );
};

const BroadcastForm = ({ users }) => {
  const [form, setForm] = useState({ title: "", body: "", body_format: "text" });
  const [audience, setAudience] = useState(emptyAudience());
  const [channels, setChannels] = useState(["dashboard"]);
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const previewHtml = broadcastPreviewHtml(form.title || "Message preview", form.body || "", form.body_format, "Squadron Staff", attachments);

  const send = async () => {
    if (!form.title || !form.body) { toast.error("Add a title and message."); return; }
    if (!audienceValid(audience)) { toast.error("Choose who this goes to."); return; }
    if (channels.length === 0) { toast.error("Choose at least one channel."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/broadcast", {
        ...form,
        audience,
        channels,
        attachment_ids: attachments.map((a) => a.id),
        base_url: BASE_URL,
      });
      toast.success(`Sent to ${data.recipients} recipient(s).`, {
        description: `${data.dashboard_delivered} to dashboards, ${data.emails_sent} email(s) sent.`,
      });
      setForm({ title: "", body: "", body_format: "text" });
      setAudience(emptyAudience());
      setAttachments([]);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not send."); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-white p-6 max-w-2xl space-y-4" data-testid="broadcast-form">
      <input data-testid="broadcast-title" className={inp} placeholder="Message title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="broadcast-format-text"
          onClick={() => setForm((f) => ({ ...f, body_format: "text" }))}
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs border transition-colors ${form.body_format === "text" ? "bg-raf-blue text-white border-raf-blue" : "bg-white text-raf-slate border-raf-sky"}`}
        >
          Plain text
        </button>
        <button
          type="button"
          data-testid="broadcast-format-html"
          onClick={() => setForm((f) => ({ ...f, body_format: "html" }))}
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs border transition-colors ${form.body_format === "html" ? "bg-raf-blue text-white border-raf-blue" : "bg-white text-raf-slate border-raf-sky"}`}
        >
          HTML
        </button>
        <button
          type="button"
          data-testid="broadcast-preview"
          onClick={() => setPreviewOpen(true)}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-raf-sky text-raf-blue hover:bg-raf-blue hover:text-white transition-colors"
        >
          <Eye size={13} /> Preview
        </button>
      </div>
      <textarea
        data-testid="broadcast-body"
        className={`${inp} font-mono`}
        rows={form.body_format === "html" ? 10 : 6}
        placeholder={form.body_format === "html" ? "Write raw HTML for the email body..." : "Write your message..."}
        value={form.body}
        onChange={(e) => setForm({ ...form, body: e.target.value })}
      />
      <p className="text-xs text-raf-slate">{form.body_format === "html" ? "HTML mode sends the markup as the email body. Use Preview to check the rendering." : "Plain text is automatically formatted for email."}</p>
      <div>
        <div className="text-xs font-semibold text-raf-navy mb-2">Recipients</div>
        <RecipientPicker value={audience} onChange={setAudience} users={users} />
      </div>
      <div>
        <div className="text-xs font-semibold text-raf-navy mb-2">Deliver via</div>
        <ChannelToggles channels={channels} setChannels={setChannels} />
      </div>
      <AttachmentPicker attachments={attachments} setAttachments={setAttachments} testId="broadcast-attachment" />
      <button data-testid="broadcast-send" onClick={send} disabled={busy} className="inline-flex items-center gap-2 px-6 py-2.5 bg-raf-red text-white font-semibold hover:bg-[#A00926] transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send
      </button>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent data-testid="broadcast-preview-dialog" className="max-w-2xl rounded-none p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="font-display text-raf-navy">Message preview</DialogTitle>
            <DialogDescription className="sr-only">Preview of the external message email</DialogDescription>
          </DialogHeader>
          <iframe title="broadcast-preview" srcDoc={previewHtml} className="w-full h-[60vh] border-0" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const NewsletterMode = ({ users }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attachmentNames, setAttachmentNames] = useState({});
  const [compose, setCompose] = useState(null); // {id?, subject, heading, intro, body}
  const [previewHtml, setPreviewHtml] = useState(null);
  const [sendFor, setSendFor] = useState(null); // newsletter being sent
  const [audience, setAudience] = useState(emptyAudience());
  const [channels, setChannels] = useState(["dashboard", "email"]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/newsletters"); setList(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/attachments")
      .then(({ data }) => {
        const map = {};
        data.forEach((a) => { map[a.id] = a.filename; });
        setAttachmentNames(map);
      })
      .catch(() => {});
  }, []);

  const saveDraft = async () => {
    if (!compose.subject || !compose.body) { toast.error("Subject and body are required."); return; }
    setBusy(true);
    try {
      const payload = {
        subject: compose.subject,
        heading: compose.heading || "",
        intro: compose.intro || "",
        body: compose.body,
        attachment_ids: compose.attachment_ids || [],
      };
      if (compose.id) await api.patch(`/newsletters/${compose.id}`, payload);
      else await api.post("/newsletters", payload);
      toast.success("Newsletter saved.");
      setCompose(null); load();
    } catch { toast.error("Could not save."); }
    finally { setBusy(false); }
  };

  const preview = async (nl) => {
    try {
      const { data } = await api.post("/newsletters/preview", {
        subject: nl.subject,
        heading: nl.heading || "",
        intro: nl.intro || "",
        body: nl.body,
        attachment_ids: nl.attachment_ids || [],
      });
      setPreviewHtml(data.html);
    } catch { toast.error("Could not build preview."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this newsletter?")) return;
    await api.delete(`/newsletters/${id}`); setList((l) => l.filter((x) => x.id !== id)); toast.success("Deleted.");
  };

  const doSend = async () => {
    if (!audienceValid(audience)) { toast.error("Choose who this goes to."); return; }
    if (channels.length === 0) { toast.error("Choose at least one channel."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/newsletters/${sendFor.id}/send`, { audience, channels, base_url: BASE_URL });
      toast.success(`Newsletter sent to ${data.recipients} recipient(s).`, {
        description: `${data.dashboard_delivered} to dashboards, ${data.emails_sent} email(s) sent.`,
      });
      setSendFor(null); setAudience(emptyAudience()); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not send."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="newsletter-mode">
      <div className="mb-4">
        <button data-testid="new-newsletter" onClick={() => setCompose({ subject: "", heading: "", intro: "", body: "", attachment_ids: [] })} className="inline-flex items-center gap-2 px-5 py-2.5 bg-raf-red text-white font-semibold hover:bg-[#A00926] transition-colors">
          <Plus size={18} /> New newsletter
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-raf-slate p-10 justify-center"><Loader2 className="animate-spin" /> Loading...</div>
      ) : list.length === 0 ? (
        <div className="bg-white p-10 text-center text-raf-slate border border-white">No newsletters yet. Create your first one.</div>
      ) : (
        <div className="space-y-3" data-testid="newsletters-list">
          {list.map((n) => (
            <div key={n.id} data-testid={`newsletter-${n.id}`} className="bg-white border border-white p-5 flex flex-wrap items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-bold text-raf-navy">{n.subject}</h3>
                  <span className={`text-[10px] uppercase px-2 py-0.5 ${n.status === "sent" ? "bg-emerald-600 text-white" : "bg-raf-sky text-raf-blue"}`}>{n.status}</span>
                </div>
                <div className="text-xs text-raf-slate mt-1">
                  {n.status === "sent" && n.result ? `Sent to ${n.result.recipients} recipient(s) on ${new Date(n.sent_at).toLocaleDateString("en-GB")}` : `Draft · ${new Date(n.created_at).toLocaleDateString("en-GB")}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`newsletter-preview-${n.id}`} onClick={() => preview(n)} className="p-2 bg-raf-sky text-raf-blue hover:bg-raf-blue hover:text-white transition-colors" title="Preview"><Eye size={15} /></button>
                <button data-testid={`newsletter-edit-${n.id}`} onClick={() => setCompose(n)} className="p-2 bg-raf-sky text-raf-blue hover:bg-raf-blue hover:text-white transition-colors" title="Edit"><Pencil size={15} /></button>
                <button data-testid={`newsletter-send-${n.id}`} onClick={() => { setSendFor(n); setChannels(["dashboard", "email"]); }} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-raf-red text-white hover:bg-[#A00926] transition-colors"><Send size={13} /> Send</button>
                <button data-testid={`newsletter-delete-${n.id}`} onClick={() => remove(n.id)} className="p-2 bg-red-50 text-raf-red hover:bg-raf-red hover:text-white transition-colors" title="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Compose dialog */}
      <Dialog open={!!compose} onOpenChange={(o) => !o && setCompose(null)}>
        <DialogContent data-testid="newsletter-compose" className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-raf-navy">{compose?.id ? "Edit newsletter" : "New newsletter"}</DialogTitle><DialogDescription className="sr-only">Compose a squadron newsletter</DialogDescription></DialogHeader>
          {compose && (
            <div className="space-y-3">
              <input data-testid="nl-subject" className={inp} placeholder="Email subject line" value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} />
              <input data-testid="nl-heading" className={inp} placeholder="Headline (shown at the top)" value={compose.heading} onChange={(e) => setCompose({ ...compose, heading: e.target.value })} />
              <textarea data-testid="nl-intro" className={inp} rows={2} placeholder="Short intro (optional)" value={compose.intro} onChange={(e) => setCompose({ ...compose, intro: e.target.value })} />
              <textarea data-testid="nl-body" className={inp} rows={8} placeholder="Newsletter content... (blank lines start a new paragraph)" value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} />
              <AttachmentPicker
                attachments={(compose.attachment_ids || []).map((id) => ({ id, filename: attachmentNames[id] || id }))}
                setAttachments={(next) => {
                  const current = (compose.attachment_ids || []).map((id) => ({ id, filename: attachmentNames[id] || id }));
                  const list = typeof next === "function"
                    ? next(current)
                    : next;
                  const map = { ...attachmentNames };
                  list.forEach((a) => { map[a.id] = a.filename; });
                  setAttachmentNames(map);
                  setCompose({ ...compose, attachment_ids: list.map((a) => a.id) });
                }}
                testId="newsletter-attachment"
              />
            </div>
          )}
          <DialogFooter className="gap-2">
            <button data-testid="nl-preview-btn" onClick={() => preview(compose)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-raf-sky text-raf-blue hover:bg-raf-blue hover:text-white transition-colors"><Eye size={16} /> Preview</button>
            <button data-testid="nl-save-btn" onClick={saveDraft} disabled={busy} className="inline-flex items-center gap-2 px-6 py-2.5 bg-raf-blue text-white font-semibold hover:bg-raf-navy transition-colors disabled:opacity-60">
              {busy && <Loader2 className="animate-spin" size={16} />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!previewHtml} onOpenChange={(o) => !o && setPreviewHtml(null)}>
        <DialogContent data-testid="newsletter-preview-dialog" className="max-w-2xl rounded-none p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5"><DialogTitle className="font-display text-raf-navy">Email preview</DialogTitle><DialogDescription className="sr-only">Preview of the newsletter email</DialogDescription></DialogHeader>
          <iframe title="preview" srcDoc={previewHtml || ""} className="w-full h-[60vh] border-0" />
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={!!sendFor} onOpenChange={(o) => !o && setSendFor(null)}>
        <DialogContent data-testid="newsletter-send-dialog" className="max-w-lg rounded-none">
          <DialogHeader><DialogTitle className="font-display text-raf-navy">Send &ldquo;{sendFor?.subject}&rdquo;</DialogTitle><DialogDescription className="sr-only">Choose recipients and channels</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-xs font-semibold text-raf-navy mb-2">Recipients</div>
              <RecipientPicker value={audience} onChange={setAudience} users={users} />
            </div>
            <div>
              <div className="text-xs font-semibold text-raf-navy mb-2">Deliver via</div>
              <ChannelToggles channels={channels} setChannels={setChannels} />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="newsletter-confirm-send" onClick={doSend} disabled={busy} className="inline-flex items-center gap-2 px-6 py-2.5 bg-raf-red text-white font-semibold hover:bg-[#A00926] transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send now
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ExternalEmailMode = () => {
  const [mailboxes, setMailboxes] = useState([]);
  const [form, setForm] = useState({
    to: "",
    subject: "",
    body: "",
    from_mailbox: "admin",
  });
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/email/mailboxes")
      .then(({ data }) => {
        setMailboxes(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length > 0) {
          setForm((current) => ({
            ...current,
            from_mailbox: current.from_mailbox || data[0].key || "admin",
          }));
        }
      })
      .catch(() => {
        setMailboxes([
          { key: "admin", label: "Admin", address: "admin@1471squadron.co.uk" },
        ]);
      });
  }, []);

  const send = async () => {
    if (!form.to.trim()) {
      toast.error("Add the recipient email address.");
      return;
    }
    if (!form.subject.trim()) {
      toast.error("Add a subject.");
      return;
    }
    if (!form.body.trim()) {
      toast.error("Write a message.");
      return;
    }

    setBusy(true);
    try {
      const { data } = await api.post("/email/send", {
        to: form.to.trim(),
        subject: form.subject.trim(),
        body: form.body.trim(),
        from_mailbox: form.from_mailbox || "admin",
        attachment_ids: attachments.map((a) => a.id),
        base_url: BASE_URL,
      });

      toast.success("Email sent.", {
        description: data.thread_id
          ? "The conversation has been added to the shared Inbox."
          : "The message was sent successfully.",
      });

      setForm((current) => ({
        ...current,
        to: "",
        subject: "",
        body: "",
      }));
      setAttachments([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "The email could not be sent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-white p-6 max-w-2xl space-y-4" data-testid="external-email-form">
      <div>
        <h2 className="font-display font-bold text-xl text-raf-navy">New external email</h2>
        <p className="text-sm text-raf-slate mt-1">
          Start a normal email conversation from a 1471 Squadron mailbox. Replies will appear in the shared Inbox.
        </p>
      </div>

      <div>
        <label className="block text-xs font-semibold text-raf-navy mb-1">From</label>
        <select
          data-testid="external-email-from"
          className={inp}
          value={form.from_mailbox}
          onChange={(e) => setForm({ ...form, from_mailbox: e.target.value })}
        >
          {mailboxes.length === 0 && <option value="admin">Admin · admin@1471squadron.co.uk</option>}
          {mailboxes.map((box) => (
            <option key={box.address || box.key} value={box.key || box.address}>
              {box.label || box.key} · {box.address}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-raf-navy mb-1">To</label>
        <input
          data-testid="external-email-to"
          type="email"
          className={inp}
          placeholder="name@example.com"
          value={form.to}
          onChange={(e) => setForm({ ...form, to: e.target.value })}
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-raf-navy mb-1">Subject</label>
        <input
          data-testid="external-email-subject"
          className={inp}
          placeholder="Email subject"
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-raf-navy mb-1">Message</label>
        <textarea
          data-testid="external-email-body"
          className={inp}
          rows={9}
          placeholder="Write your email..."
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
        />
      </div>

      <AttachmentPicker
        attachments={attachments}
        setAttachments={setAttachments}
        testId="external-email-attachment"
      />

      <div className="bg-raf-sky/40 border-l-4 border-raf-blue px-4 py-3 text-sm text-raf-slate">
        Replies to this message will return to the selected squadron mailbox and will be shown in Communications → Inbox.
      </div>

      <button
        data-testid="external-email-send"
        type="button"
        onClick={send}
        disabled={busy}
        className="inline-flex items-center gap-2 px-6 py-2.5 bg-raf-red text-white font-semibold hover:bg-[#A00926] transition-colors disabled:opacity-60"
      >
        {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
        Send email
      </button>
    </div>
  );
};

export const CommsPanel = () => {
  const [mode, setMode] = useState("message");
  const [users, setUsers] = useState([]);

  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);

  return (
    <div>
      <PanelHeading title="Communications" intro="Send squadron notifications, start external email conversations, or compose and send a newsletter." />
      <div className="flex gap-2 mb-6 flex-wrap">
        <button data-testid="comms-mode-message" onClick={() => setMode("message")} className={`inline-flex items-center gap-2 px-4 py-2 text-sm transition-colors ${mode === "message" ? "bg-raf-blue text-white" : "bg-white text-raf-slate hover:text-raf-blue"}`}>
          <MessageSquare size={16} /> Squadron message
        </button>
        <button data-testid="comms-mode-email" onClick={() => setMode("email")} className={`inline-flex items-center gap-2 px-4 py-2 text-sm transition-colors ${mode === "email" ? "bg-raf-blue text-white" : "bg-white text-raf-slate hover:text-raf-blue"}`}>
          <Mail size={16} /> External email
        </button>
        <button data-testid="comms-mode-newsletter" onClick={() => setMode("newsletter")} className={`inline-flex items-center gap-2 px-4 py-2 text-sm transition-colors ${mode === "newsletter" ? "bg-raf-blue text-white" : "bg-white text-raf-slate hover:text-raf-blue"}`}>
          <Newspaper size={16} /> Newsletter
        </button>
      </div>

      {mode === "message" && <BroadcastForm users={users} />}
      {mode === "email" && <ExternalEmailMode />}
      {mode === "newsletter" && <NewsletterMode users={users} />}
    </div>
  );
};
